<template>
  <section v-if="available" class="open-house" aria-labelledby="open-house-title">
    <header class="oh-header">
      <span class="oh-icon"><v-icon color="#70dfa0">mdi-door-open</v-icon></span>
      <div class="oh-heading">
        <span class="oh-meta">31. Oktober – 1. November 2026</span>
        <h2 id="open-house-title">Tag der offenen Tür</h2>
        <p v-if="data">Studio {{ data.studio.name }} <span class="oh-badge" :class="{ 'oh-badge--success': isConfirmed }">{{ ownStatus }}</span></p>
      </div>
      <button type="button" class="oh-button" :aria-expanded="String(expanded)" aria-controls="open-house-content" @click="expanded = !expanded">
        {{ expanded ? 'Schliessen' : 'Öffnen' }}
        <v-icon small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </button>
    </header>
    <div v-if="data && data.canManage" class="oh-management-bar">
      <label class="oh-visibility">
        <input type="checkbox" role="switch" :checked="data.enabledForCenters" :aria-checked="String(data.enabledForCenters)" :disabled="blocked" @change="toggleVisibility($event)" />
        <span class="oh-switch-track" aria-hidden="true"></span>
        <span>Für Centerleitungen sichtbar<small>{{ data.enabledForCenters ? 'Freigeschaltet' : 'Ausgeblendet · nur Geschäftsleitung' }}</small></span>
      </label>
    </div>

    <div v-if="loading && !data" class="oh-message" role="status">Angaben werden geladen …</div>
    <div v-if="error" class="oh-message oh-error" role="alert">
      <span>{{ error }}</span>
      <button type="button" class="oh-button" :disabled="loading || busy" @click="load(true)">Neu laden</button>
    </div>
    <div v-if="success" class="oh-message oh-success" role="status">{{ success }}</div>

    <div v-if="expanded && data" id="open-house-content" class="oh-content">
      <nav v-if="data.canManage" class="oh-tabs" aria-label="Bereiche Tag der offenen Tür">
        <button v-for="item in tabs" :key="item.id" type="button" :class="{ active: tab === item.id || (tab === 'studio' && item.id === 'overview') }" :aria-pressed="String(tab === item.id || (tab === 'studio' && item.id === 'overview'))" :disabled="tab === 'studio' && hasLocalChanges" @click="tab = item.id">{{ item.label }}</button>
      </nav>

      <div v-if="tab === 'studio'">
        <button v-if="data.canManage" type="button" class="oh-button" :disabled="blocked || hasLocalChanges" @click="tab = 'overview'">
          <v-icon small>mdi-arrow-left</v-icon> Zur Studio-Übersicht
        </button>
        <div v-if="data.canManage" class="oh-form oh-studio-picker">
          <label>Angaben bearbeiten für<select :value="data.studio.id" :disabled="blocked || hasLocalChanges" @change="changeStudio($event.target.value)">
            <option v-if="!overviewStudios.some(studio => studio.id === data.studio.id)" :value="data.studio.id">{{ data.studio.name }} (angemeldet)</option>
            <option v-for="studio in overviewStudios" :key="studio.id" :value="studio.id">{{ studio.name }}</option>
          </select></label>
          <p v-if="hasLocalChanges" class="oh-secondary">Speichere oder verwirf deine Eingaben, bevor du das Studio wechselst oder zur Übersicht zurückkehrst.</p>
        </div>
        <p v-if="loading" class="oh-secondary" role="status">Angaben werden aktualisiert …</p>
        <p v-if="needsConfirmation" class="oh-notice">Der gemeinsame Katalog wurde aktualisiert. Bitte prüfe eure Auswahl und bestätige die Angaben erneut.</p>
        <div v-if="!questionnaireOpen" class="oh-answer-summary">
          <div class="oh-row">
            <div>
              <h3>{{ summaryTitle }}</h3>
              <p v-if="data.selection.confirmed" class="oh-secondary">Bestätigt am {{ formatDate(data.selection.confirmedAt) }} · {{ selected.length }} Punkte aktiviert<span v-if="noteCount(notes)"> · {{ noteCount(notes) }} eigene Angaben</span></p>
              <p v-else class="oh-secondary">{{ data.selection.updatedAt ? 'Deine bisherige Auswahl ist gespeichert.' : `${data.categories.length} kurze Fragen zu euren Aktivitäten am Tag der offenen Tür.` }}</p>
            </div>
            <button type="button" class="oh-button oh-button--primary" :disabled="blocked" @click="startQuestionnaire">{{ data.selection.confirmed ? 'Bearbeiten' : data.selection.updatedAt ? 'Weiter ausfüllen' : 'Fragebogen starten' }}</button>
          </div>
          <div v-if="data.selection.updatedAt" class="oh-summary-groups">
            <div v-for="group in selectedGroups" :key="group.id">
              <h4>{{ group.title }}</h4><ul class="oh-summary-points"><li v-for="point in group.points" :key="point.id">{{ point.title }}</li><li v-if="group.note">{{ group.note }} <span class="oh-meta">(eigener Punkt)</span></li></ul>
            </div>
            <p v-if="!selectedGroups.length" class="oh-secondary">Keine Angaben erfasst.</p>
          </div>
        </div>
        <div v-else class="oh-questionnaire">
          <div class="oh-row">
            <span class="oh-badge">{{ reviewingAnswers ? 'Auswahl prüfen' : `Frage ${step + 1} von ${data.categories.length}` }}</span>
            <button type="button" class="oh-button" :disabled="blocked" @click="discardAnswers">{{ dirty ? 'Änderungen verwerfen' : 'Schliessen' }}</button>
          </div>
          <progress class="oh-progress" :value="step + 1" :max="data.categories.length + 1" aria-label="Fortschritt im Fragebogen"></progress>
          <div v-if="!reviewingAnswers && activeCategory" :key="activeCategory.id">
            <span class="oh-meta">{{ activeCategory.area }}</span>
            <h3 ref="questionTitle" tabindex="-1">{{ questionTitle }}</h3>
            <p class="oh-secondary">Wähle alles aus, was zutrifft. Du kannst auch ohne Auswahl weitergehen.</p>
            <fieldset class="oh-question-options" :disabled="blocked">
              <legend>{{ activeCategory.title }}</legend>
              <div class="oh-options-grid">
                <label v-for="point in pointsFor(activeCategory.id)" :key="point.id" class="oh-option">
                  <input v-model="selected" type="checkbox" :value="point.id" @change="success = ''" />
                  <span class="oh-option-surface">
                    <span class="oh-option-title">{{ point.title }}</span>
                    <span class="oh-option-mark" aria-hidden="true"></span>
                  </span>
                </label>
              </div>
              <p v-if="!pointsFor(activeCategory.id).length" class="oh-secondary">Die Geschäftsleitung hat hier noch keine Punkte erfasst. Du kannst eigene Angaben ergänzen oder weitergehen.</p>
            </fieldset>
            <div class="oh-form oh-category-note">
              <label :for="'oh-note-' + activeCategory.id">Eigener Punkt (optional)
                <input :id="'oh-note-' + activeCategory.id" v-model="notes[activeCategory.id]" type="text" maxlength="100" :disabled="blocked" :aria-invalid="String(wordCount(notes[activeCategory.id]) > 5)" :aria-describedby="'oh-note-help-' + activeCategory.id" placeholder="Zum Beispiel: Frische Smoothies" @input="success = ''" />
              </label>
              <div class="oh-row"><span :id="'oh-note-help-' + activeCategory.id" class="oh-secondary">Maximal 5 Wörter · nur für euer Studio.</span><span class="oh-meta" :class="{ 'oh-error': wordCount(notes[activeCategory.id]) > 5 }">{{ wordCount(notes[activeCategory.id]) }} / 5 Wörter</span></div>
              <p v-if="wordCount(notes[activeCategory.id]) > 5" class="oh-error oh-message" role="alert">Bitte auf maximal 5 Wörter kürzen.</p>
            </div>
          </div>
          <div v-else>
            <h3 ref="questionTitle" tabindex="-1">Passt die Auswahl für {{ data.studio.name }}?</h3>
            <p class="oh-secondary">{{ selected.length }} Punkte ausgewählt<span v-if="noteCount(notes)"> · {{ noteCount(notes) }} eigene Angaben</span>. Mit deiner Bestätigung sind die Angaben vollständig.</p>
            <div class="oh-summary-groups">
              <div v-for="group in selectedGroups" :key="group.id">
              <h4>{{ group.title }}</h4><ul class="oh-summary-points"><li v-for="point in group.points" :key="point.id">{{ point.title }}</li><li v-if="group.note">{{ group.note }} <span class="oh-meta">(eigener Punkt)</span></li></ul>
            </div>
              <p v-if="!selectedGroups.length" class="oh-notice">Du hast keine Punkte ausgewählt und keine eigenen Angaben erfasst. Auch diese leeren Angaben kannst du bestätigen.</p>
            </div>
          </div>
          <div class="oh-question-actions">
            <button type="button" class="oh-button" :disabled="blocked || step === 0" @click="moveStep(-1)">Zurück</button>
            <button type="button" class="oh-button" :disabled="blocked || invalidNotes" @click="saveSelection(false)">Später fortsetzen</button>
            <button v-if="!reviewingAnswers" type="button" class="oh-button oh-button--primary" :disabled="blocked || invalidNotes" @click="moveStep(1)">{{ step === data.categories.length - 1 ? 'Auswahl prüfen' : 'Weiter' }}</button>
            <button v-else type="button" class="oh-button oh-button--primary" :disabled="blocked || invalidNotes" @click="saveSelection(true)">{{ busy === 'selection' ? 'Wird gespeichert …' : 'Angaben bestätigen' }}</button>
          </div>
        </div>

      </div>

      <div v-if="tab === 'overview' && data.canManage">
        <div class="oh-row"><h3>Studio-Übersicht</h3><span class="oh-badge">{{ confirmedCount }} / {{ overviewStudios.length }} bestätigt</span></div>
        <div class="oh-studio-list">
          <details v-for="studio in overviewStudios" :key="studio.id" class="oh-studio">
            <summary><span class="oh-studio-name">{{ studio.name }}</span><span class="oh-badge" :class="{ 'oh-badge--success': studio.confirmed }">{{ studioStatus(studio) }}</span><span class="oh-meta">{{ studio.selected.length + noteCount(studio.notes) }} Angaben</span></summary>
            <div class="oh-studio-detail">
              <p v-if="studio.updatedAt" class="oh-secondary">Zuletzt gespeichert: {{ formatDate(studio.updatedAt) }}</p>
              <p v-if="studio.confirmed" class="oh-secondary">Bestätigt: {{ formatDate(studio.confirmedAt) }}</p>
              <div class="oh-summary-groups">
                <div v-for="group in answerGroups(studio.selected, studio.notes)" :key="group.id">
                  <h4>{{ group.title }}</h4><ul class="oh-summary-points"><li v-for="point in group.points" :key="point.id">{{ point.title }}</li><li v-if="group.note">{{ group.note }} <span class="oh-meta">(eigener Punkt)</span></li></ul>
                </div>
              </div>
              <p v-if="!studio.selected.length && !noteCount(studio.notes)" class="oh-secondary">{{ studio.confirmed ? 'Das Studio hat keine Aktivitäten angegeben und dies bestätigt.' : 'Noch keine Angaben gespeichert.' }}</p>
              <button type="button" class="oh-button" :disabled="blocked || hasLocalChanges" @click="changeStudio(studio.id, true)">Angaben bearbeiten</button>
            </div>
          </details>
        </div>
        <p v-if="!overviewStudios.length" class="oh-secondary">Keine relevanten Studios vorhanden.</p>
      </div>

      <div v-if="tab === 'catalog' && data.canManage">
        <p class="oh-secondary">Erfasse die Aktivitäten für den Tag der offenen Tür. Die Center wählen aus diesen gemeinsamen Punkten aus.</p>
        <div class="oh-row"><h3>Gemeinsame Punkte</h3><div class="oh-actions">
          <button v-if="data.canReset" type="button" class="oh-button oh-button--danger" :disabled="blocked || hasLocalChanges" @click="resetOpen = true">Punkte zurücksetzen</button>
          <button type="button" class="oh-button oh-button--primary" :disabled="blocked" @click="openPoint()">Punkt hinzufügen</button>
        </div></div>
        <details class="oh-catalog-help"><summary class="oh-secondary">Was passiert bei Änderungen?</summary><p class="oh-secondary">Änderungen gelten für alle Studios. Bestehende Auswahlen bleiben erhalten; die Studios müssen ihre Angaben danach erneut bestätigen.</p></details>
        <div class="oh-catalog-layout">
          <nav class="oh-catalog-nav" aria-label="Punkte nach Kategorie">
            <button type="button" :class="{ active: catalogCategory === 'all' && !catalogQuery.trim() }" :aria-pressed="String(catalogCategory === 'all' && !catalogQuery.trim())" @click="selectCatalogCategory('all')"><span>Alle Punkte</span><span class="oh-badge">{{ data.points.length }}</span></button>
            <div v-for="group in catalogGroups" :key="group.area" class="oh-catalog-group">
              <h4>{{ group.area }}</h4>
              <button v-for="category in group.categories" :key="category.id" type="button" :class="{ active: catalogCategory === category.id && !catalogQuery.trim() }" :aria-pressed="String(catalogCategory === category.id && !catalogQuery.trim())" @click="selectCatalogCategory(category.id)"><span>{{ category.title }}</span><span class="oh-badge">{{ category.count }}</span></button>
            </div>
          </nav>
          <div class="oh-catalog-main">
            <div class="oh-catalog-tools oh-form">
              <label class="oh-catalog-mobile-filter">Kategorie<select :value="catalogCategory" @change="selectCatalogCategory($event.target.value)"><option value="all">Alle Punkte ({{ data.points.length }})</option><optgroup v-for="group in catalogGroups" :key="group.area" :label="group.area"><option v-for="category in group.categories" :key="category.id" :value="category.id">{{ category.title }} ({{ category.count }})</option></optgroup></select></label>
              <label>Punkt suchen<input v-model="catalogQuery" type="search" placeholder="Alle Kategorien durchsuchen …" @input="catalogOpenPoint = ''" /></label>
            </div>
            <div class="oh-row oh-catalog-heading"><h4>{{ catalogHeading }}</h4><span class="oh-meta" role="status">{{ catalogPoints.length }} {{ catalogPoints.length === 1 ? 'Punkt' : 'Punkte' }}</span></div>
            <div v-if="catalogPoints.length" class="oh-catalog-list">
              <article v-for="point in catalogPoints" :key="point.id" class="oh-catalog-item">
                <div class="oh-catalog-row">
                  <div class="oh-catalog-title"><strong>{{ point.title }}</strong><span v-if="catalogQuery.trim() || catalogCategory === 'all'" class="oh-meta">{{ point.categoryTitle }}</span></div>
                  <button type="button" class="oh-catalog-usage" :disabled="!point.studios.length" :aria-expanded="String(catalogOpenPoint === point.id)" :aria-controls="'oh-point-studios-' + point.id" :aria-label="point.studios.length + ' Studios haben ' + point.title + ' aktiviert'" @click="catalogOpenPoint = catalogOpenPoint === point.id ? '' : point.id">
                    <v-icon small>mdi-store-outline</v-icon><span>{{ point.studios.length }} {{ point.studios.length === 1 ? 'Studio' : 'Studios' }}</span><v-icon v-if="point.studios.length" small>{{ catalogOpenPoint === point.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </button>
                  <button type="button" class="oh-button oh-catalog-edit" :disabled="blocked" :aria-label="point.title + ' bearbeiten'" :title="point.title + ' bearbeiten'" @click="openPoint(point)"><v-icon small>mdi-pencil-outline</v-icon></button>
                </div>
                <div v-if="catalogOpenPoint === point.id && point.studios.length" :id="'oh-point-studios-' + point.id" class="oh-catalog-studios"><span v-for="studio in point.studios" :key="studio.id" class="oh-badge">{{ studio.name }}</span></div>
              </article>
            </div>
            <p v-else class="oh-notice">{{ catalogQuery.trim() ? 'Keine passenden Punkte gefunden.' : 'In dieser Kategorie gibt es noch keine Punkte.' }}</p>
          </div>
        </div>
        <details v-if="data.archivedPoints && data.archivedPoints.length" class="oh-catalog-help">
          <summary class="oh-secondary">Bisherige Studio-Punkte ({{ data.archivedPoints.length }})</summary>
          <p class="oh-secondary">Diese Punkte stehen den Centern nicht mehr zur Auswahl. Falls ein Punkt als TDOT-Aktivität weiterverwendet werden soll, ordne ihn einer neuen Kategorie zu.</p>
          <div v-for="point in data.archivedPoints" :key="point.id" class="oh-row oh-archived-point">
            <span>{{ point.title }}</span><button type="button" class="oh-button" :disabled="blocked" @click="openPoint(point)">Kategorie zuordnen</button>
          </div>
        </details>
      </div>

      <div class="oh-refresh"><span class="oh-meta">Sichtbar bis und mit 1. November 2026</span><button type="button" class="oh-button" :disabled="blocked" @click="load(true)">{{ loading ? 'Wird aktualisiert …' : 'Aktualisieren' }}</button></div>
    </div>

    <v-dialog v-if="data && data.canReset" v-model="resetOpen" max-width="560" persistent>
      <v-card dark class="oh-dialog">
        <v-card-title><h3>Alle Punkte zurücksetzen?</h3></v-card-title>
        <v-card-text>
          <p>Alle Punkte, inklusive der bisherigen Studio-Punkte, werden für alle Studios gelöscht. Auch die gespeicherten Auswahlen, eigenen Angaben und Bestätigungen der Studios werden zurückgesetzt.</p>
          <p>Die fünf Kategorien und die Sichtbarkeit für Centerleitungen bleiben erhalten. Dieser Schritt kann nicht rückgängig gemacht werden.</p>
          <p v-if="error" role="alert" class="oh-error oh-message">{{ error }}</p>
          <button v-if="needsReload" type="button" class="oh-button" :disabled="loading || !!busy" @click="reloadReset">Aktuelle Angaben laden</button>
        </v-card-text>
        <v-card-actions class="oh-dialog-actions">
          <button type="button" class="oh-button" :disabled="!!busy" @click="resetOpen = false">Abbrechen</button>
          <button type="button" class="oh-button oh-button--danger" :disabled="blocked" @click="resetPoints">{{ busy === 'reset' ? 'Wird zurückgesetzt …' : 'Alle Punkte endgültig löschen' }}</button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="data && data.canManage" v-model="editorOpen" max-width="640" persistent scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card dark class="oh-dialog">
        <v-card-title><h3>{{ editor.id ? 'Punkt bearbeiten' : 'Punkt hinzufügen' }}</h3></v-card-title>
        <v-card-text>
          <form id="open-house-editor" class="oh-form" @submit.prevent="saveEditor">
            <label>Titel für alle Studios<input v-model="editor.title" required maxlength="140" :disabled="blocked" /></label>
            <label>Kategorie<select v-model="editor.category" required :disabled="blocked"><option value="" disabled>Bitte wählen</option><option v-for="category in data.categories" :key="category.id" :value="category.id">{{ category.title }}</option></select></label>
            <p v-if="editorError" role="alert" class="oh-error oh-message">{{ editorError }}</p>
            <button v-if="needsReload" type="button" class="oh-button" :disabled="loading || !!busy" @click="load(true)">Aktuelle Angaben laden</button>
          </form>
        </v-card-text>
        <v-card-actions class="oh-dialog-actions"><button type="button" class="oh-button" :disabled="!!busy" @click="editorOpen = false">Abbrechen</button><button form="open-house-editor" type="submit" class="oh-button oh-button--primary" :disabled="blocked">{{ busy ? 'Wird gespeichert …' : 'Punkt speichern' }}</button></v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"
import { mapState } from "vuex"

const CLOSES_AT = new Date("2026-11-02T00:00:00+01:00").getTime()
const QUESTIONS = {
  food: "Was bietet ihr am Tag der offenen Tür zum Essen und Trinken an?",
  advice: "Welche Beratungen und Analysen bietet ihr am Tag der offenen Tür an?",
  action: "Welche Aktionen und Aktivitäten plant ihr am Tag der offenen Tür?",
  extras: "Welche Extras und Überraschungen gibt es am Tag der offenen Tür?",
  kids: "Was bietet ihr am Tag der offenen Tür für Kinder an?",
}
const categoryNotes = (categories, notes = {}) => Object.fromEntries(categories.map(({ id }) => [id, notes[id] || ""]))
const emptyEditor = () => ({ id: "", title: "", category: "" })

export default {
  name: "OpenHousePanel",
  data: () => ({ now: Date.now(), closed: false, hidden: true, data: null, selected: [], notes: {}, expanded: false, tab: "studio", loading: false,
    questionnaireOpen: false, step: 0, accessTimer: null, checkingAccess: false,
    catalogCategory: "food", catalogQuery: "", catalogOpenPoint: "",
    busy: "", error: "", success: "", needsReload: false,
    resetOpen: false, editorOpen: false, editor: emptyEditor(), editorError: "", requestId: 0, disposed: false, timer: null, expiryTimer: null }),
  computed: {
    ...mapState("studioStore", ["selectedStudio"]),
    studioKey() { return this.selectedStudio ? this.selectedStudio._id : "" },
    eligible() { return !this.closed && this.now < CLOSES_AT && userService.hasRight("aka_teacher", "aka_allAudits") },
    available() { return this.eligible && !this.hidden },
    blocked() { return !!this.busy || this.loading || this.needsReload || !this.available },
    dirty() {
      return !!this.data && ([...this.selected].sort().join("|") !== [...this.data.selection.selected].sort().join("|")
        || this.data.categories.some(({ id }) => (this.notes[id] || "") !== ((this.data.selection.notes || {})[id] || "")))
    },
    isConfirmed() { return !!this.data && this.data.selection.confirmed && !this.dirty },
    needsConfirmation() { return !!this.data && !!this.data.selection.confirmedAt && !this.data.selection.confirmed },
    ownStatus() { return this.dirty ? "Ungespeicherte Änderungen" : this.data ? this.studioStatus(this.data.selection) : "" },
    invalidNotes() { return Object.values(this.notes).some(note => note.length > 100 || this.wordCount(note) > 5) },
    hasLocalChanges() { return this.dirty },
    activeCategory() { return this.data ? this.data.categories[this.step] : null },
    reviewingAnswers() { return !!this.data && this.step >= this.data.categories.length },
    questionTitle() { return this.activeCategory ? QUESTIONS[this.activeCategory.id] || this.activeCategory.title : "" },
    selectedGroups() { return this.answerGroups(this.selected, this.notes) },
    summaryTitle() {
      if (!this.data) return ""
      if (this.needsConfirmation) return "Bitte prüfe die Angaben erneut."
      if (this.isConfirmed) return this.data.canManage ? `Die Angaben für ${this.data.studio.name} sind bestätigt.` : "Du hast den Fragebogen bereits ausgefüllt."
      return this.data.selection.updatedAt ? "Dein Entwurf ist gespeichert." : "Was plant ihr am Tag der offenen Tür?"
    },
    overviewStudios() {
      return this.data ? this.data.studios.filter(studio => !/workout/i.test(studio.name || "")
        && !["test", "ot", "alibey"].includes(String(studio.code || "").trim().toLowerCase())) : []
    },
    confirmedCount() { return this.overviewStudios.filter(studio => studio.confirmed).length },
    catalogGroups() {
      if (!this.data) return []
      return [...new Set(this.data.categories.map(category => category.area))].map(area => ({ area,
        categories: this.data.categories.filter(category => category.area === area).map(category => ({ ...category, count: this.pointsFor(category.id).length })) }))
    },
    catalogHeading() {
      if (this.catalogQuery.trim()) return "Suchergebnisse"
      const category = this.data && this.data.categories.find(category => category.id === this.catalogCategory)
      return category ? category.title : "Alle Punkte"
    },
    catalogPoints() {
      if (!this.data) return []
      const query = this.catalogQuery.trim().toLocaleLowerCase("de-CH")
      return this.data.points.map(point => {
        const category = this.data.categories.find(category => category.id === point.category)
        return { ...point, categoryTitle: category ? category.title : "", area: category ? category.area : "" }
      }).filter(point => query ? `${point.title} ${point.categoryTitle} ${point.area}`.toLocaleLowerCase("de-CH").includes(query)
        : this.catalogCategory === "all" || point.category === this.catalogCategory)
        .map(point => ({ ...point, studios: this.studiosFor(point.id) }))
    },
    tabs() { return [{ id: "overview", label: "Studio-Übersicht" },
      { id: "catalog", label: "Punkte verwalten" }] },
  },
  watch: {
    studioKey() { this.reset(); this.load() },
  },
  mounted() {
    this.load()
    this.timer = setInterval(this.checkTime, 30000)
    this.accessTimer = setInterval(this.refreshAvailability, 60000)
    this.scheduleExpiry()
    document.addEventListener("visibilitychange", this.onVisibility)
  },
  beforeDestroy() {
    this.disposed = true
    this.requestId++
    clearInterval(this.timer)
    clearInterval(this.accessTimer)
    clearTimeout(this.expiryTimer)
    document.removeEventListener("visibilitychange", this.onVisibility)
  },
  methods: {
    reset() {
      this.requestId++; this.data = null; this.selected = []; this.notes = {}; this.error = ""; this.success = ""; this.needsReload = false
      this.resetOpen = false; this.editorOpen = false; this.editorError = ""; this.tab = "studio"
      this.busy = ""; this.loading = false
      this.questionnaireOpen = false; this.step = 0; this.hidden = true
      this.catalogCategory = "food"; this.catalogQuery = ""; this.catalogOpenPoint = ""
    },
    checkTime() { this.now = Date.now(); if (!this.eligible) { this.reset(); this.expanded = false } },
    scheduleExpiry() {
      clearTimeout(this.expiryTimer)
      const remaining = CLOSES_AT - Date.now()
      if (remaining <= 0) { this.checkTime(); return }
      this.expiryTimer = setTimeout(() => { this.checkTime(); if (this.eligible) this.scheduleExpiry() }, Math.min(remaining, 2147483647))
    },
    onVisibility() { if (!document.hidden) { this.checkTime(); this.scheduleExpiry(); this.refreshAvailability() } },
    async refreshAvailability() {
      this.checkTime()
      if (!this.eligible || document.hidden || this.busy || this.loading || this.checkingAccess) return
      this.checkingAccess = true
      const requestId = this.requestId
      try {
        const access = await api.openHouse.availability()
        if (this.disposed || requestId !== this.requestId || !this.eligible) return
        if (!access.visible) { this.reset(); this.expanded = false; return }
        if (this.hidden || !this.data) await this.load()
        else this.data.enabledForCenters = access.enabledForCenters
      } catch (error) {
        if (!this.disposed && requestId === this.requestId && error.response && [401, 403, 410].includes(error.response.status)) this.handleError(error)
      } finally { this.checkingAccess = false }
    },
    wordCount(value = "") { return value.trim() ? value.trim().split(/\s+/).length : 0 },
    noteCount(notes = {}) { return Object.values(notes).filter(note => typeof note === "string" && note.trim()).length },
    answerGroups(selected = [], notes = {}) {
      return this.data ? this.data.categories.map(category => ({ ...category, note: (notes[category.id] || "").trim(),
        points: this.data.points.filter(point => point.category === category.id && selected.includes(point.id)) })).filter(group => group.points.length || group.note) : []
    },
    pointsFor(category) { return this.data.points.filter(point => point.category === category) },
    selectCatalogCategory(id) { this.catalogCategory = id; this.catalogQuery = ""; this.catalogOpenPoint = "" },
    pointById(id) { return this.data.points.find(point => point.id === id) },
    studiosFor(id) { return this.data.studios.filter(studio => studio.selected.includes(id)) },
    studioStatus(studio) { return studio.confirmed ? "Bestätigt" : studio.confirmedAt ? "Erneut bestätigen" : studio.updatedAt ? "Entwurf" : "Noch nicht ausgefüllt" },
    formatDate(value) { return new Date(value).toLocaleString("de-CH", { timeZone: "Europe/Zurich", dateStyle: "short", timeStyle: "short" }) },
    accept(data, preserveDraft) {
      if (!data.visible) { this.reset(); this.expanded = false; return }
      const enteringManagement = data.canManage && (!this.data || !this.data.canManage)
      const keep = preserveDraft && this.data && this.data.studio.id === data.studio.id && this.dirty
      this.selected = keep ? this.selected.filter(id => data.points.some(point => point.id === id)) : [...data.selection.selected]
      this.notes = categoryNotes(data.categories, keep ? this.notes : data.selection.notes)
      this.data = data
      this.hidden = false
      if (enteringManagement && this.tab === "studio") this.tab = "overview"
      if (!data.canReset) this.resetOpen = false
      if (!data.canManage) { this.tab = "studio"; this.editorOpen = false }
    },
    handleError(error) {
      const status = error.response && error.response.status
      if (status === 410) { this.closed = true; this.reset(); return }
      if (status === 401 || status === 403) { this.reset(); this.expanded = false; return }
      const message = error.response && error.response.data && error.response.data.error
      this.error = message || "Die Angaben konnten nicht geladen oder gespeichert werden. Bitte erneut versuchen."
      this.hidden = false
      if (status === 409) this.needsReload = true
      if (this.editorOpen) this.editorError = this.error
    },
    async load(preserveDraft = false, targetId = this.data ? this.data.studio.id : undefined) {
      this.checkTime()
      if (!this.eligible || this.busy) return false
      const requestId = ++this.requestId
      this.loading = true; this.error = ""
      try {
        const data = await api.openHouse.load(targetId)
        if (this.disposed || requestId !== this.requestId || !this.eligible) return false
        this.accept(data, preserveDraft); this.needsReload = false; this.editorError = ""
        return this.available
      } catch (error) { if (!this.disposed && requestId === this.requestId) this.handleError(error) }
      finally { if (!this.disposed && requestId === this.requestId) this.loading = false }
    },
    async mutate(action, input) {
      if (this.blocked || !this.data) return false
      const requestId = ++this.requestId
      this.busy = action; this.error = ""; this.success = ""; this.editorError = ""
      try {
        const data = await api.openHouse.save(action, { ...input, revision: this.data.revision,
          studioId: this.data.sessionStudioId, targetStudioId: this.data.studio.id })
        if (this.disposed || requestId !== this.requestId || !this.available) return false
        this.accept(data, !["selection", "reset"].includes(action))
        return true
      } catch (error) { if (!this.disposed && requestId === this.requestId) this.handleError(error); return false }
      finally { if (!this.disposed && requestId === this.requestId) this.busy = "" }
    },
    async saveSelection(confirm) {
      if (this.invalidNotes) return
      if (await this.mutate("selection", { selected: [...this.selected], notes: { ...this.notes }, confirm })) {
        this.questionnaireOpen = false; this.step = 0
        if (this.data.canManage && !this.hasLocalChanges) this.tab = "overview"
        this.success = confirm ? `Die Angaben für ${this.data.studio.name} sind bestätigt.` : "Dein Entwurf ist gespeichert. Du kannst später weiter ausfüllen."
      }
    },
    startQuestionnaire() { this.questionnaireOpen = true; this.step = 0; this.success = "" },
    moveStep(direction) {
      if (this.blocked || (direction > 0 && this.invalidNotes)) return
      this.step = Math.max(0, Math.min(this.data.categories.length, this.step + direction))
      this.$nextTick(() => { if (this.$refs.questionTitle) this.$refs.questionTitle.focus() })
    },
    discardAnswers() {
      this.selected = [...this.data.selection.selected]; this.notes = categoryNotes(this.data.categories, this.data.selection.notes); this.questionnaireOpen = false; this.step = 0; this.success = ""
      if (this.data.canManage && !this.hasLocalChanges) this.tab = "overview"
    },
    async changeStudio(id, edit = false) {
      if (this.blocked || this.hasLocalChanges || !this.data.canManage) return
      if (await this.load(false, id)) { this.tab = "studio"; this.questionnaireOpen = edit; this.step = 0; this.success = "" }
    },
    async toggleVisibility(event) {
      const enabledForCenters = event.target.checked
      event.target.checked = this.data.enabledForCenters
      if (await this.mutate("visibility", { enabledForCenters })) this.success = enabledForCenters ? "Der Bereich ist jetzt für die Centerleitungen freigeschaltet." : "Der Bereich ist jetzt nur für die Geschäftsleitung sichtbar."
    },
    async reloadReset() {
      this.resetOpen = false
      await this.load(true)
    },
    async resetPoints() {
      if (!this.resetOpen || !this.data || !this.data.canReset) return
      if (await this.mutate("reset", { confirm: true })) {
        this.resetOpen = false; this.questionnaireOpen = false; this.step = 0
        this.selectCatalogCategory("food")
        this.success = "Alle Punkte, Studio-Auswahlen und eigenen Angaben wurden zurückgesetzt. Die fünf Kategorien bleiben erhalten."
      }
    },
    openPoint(point) {
      this.editor = { ...emptyEditor(), id: point ? point.id : "", title: point ? point.title : "",
        category: point ? (this.data.categories.some(category => category.id === point.category) ? point.category : "") : this.catalogCategory === "all" ? "" : this.catalogCategory }
      this.editorError = ""; this.editorOpen = true
    },
    async saveEditor() {
      if (await this.mutate("point", this.editor)) {
        this.selectCatalogCategory(this.editor.category)
        this.editorOpen = false; this.success = "Der gemeinsame Punkt ist gespeichert."
      }
    },
  },
}
</script>

<style scoped>
.open-house { margin-top: 1rem; border: 1px solid rgba(112, 223, 160, .28); border-radius: 1rem; background: linear-gradient(125deg, rgba(0, 150, 64, .12), rgba(255, 255, 255, .025)); color: #f5f8f6; font-size: 1rem; line-height: 1.5; }
.oh-header { display: flex; align-items: center; gap: 1rem; padding: 1.25rem; flex-wrap: wrap; }
.oh-icon { display: grid; place-items: center; width: 3rem; height: 3rem; border-radius: .875rem; background: rgba(112, 223, 160, .1); }
.oh-heading { flex: 1; min-width: 12rem; }
.open-house h2 { font-size: 1.25rem; font-weight: 600; margin: .125rem 0 .375rem; }
.open-house h3, .oh-dialog h3 { font-size: 1.125rem; font-weight: 600; margin: 0; }
.open-house h4 { font-size: 1rem; font-weight: 600; margin: 1rem 0 .75rem; }
.open-house p, .oh-dialog p { margin: .5rem 0; }
.oh-heading p { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; margin: 0; }
.oh-meta { font-size: .75rem; color: #b7c7be; }
.oh-secondary { font-size: .875rem; color: #c5d0ca; }
.oh-badge { display: inline-block; width: fit-content; font-size: .75rem; line-height: 1.5; padding: .2rem .6rem; border-radius: .5rem; background: rgba(255, 255, 255, .08); color: #d3ddd7; }
.oh-badge--success { background: rgba(112, 223, 160, .15); color: #95ecba; }
.oh-button { display: inline-flex; align-items: center; justify-content: center; gap: .4rem; min-height: 2.75rem; border: 1px solid #53675c; border-radius: .65rem; padding: .625rem .875rem; font-family: inherit; font-size: .875rem; font-weight: 500; line-height: 1.4; color: #f5f8f6; background: rgba(255, 255, 255, .04); cursor: pointer; white-space: normal; }
.oh-button--danger { color: #ffc9c4; border-color: #b9736b; background: rgba(190, 60, 45, .12); }
.oh-button--primary { color: #092415; background: #70dfa0; border-color: #70dfa0; }
.oh-management-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: .75rem 1.25rem; padding: 0 1.25rem 1rem; }
.oh-visibility { position: relative; display: flex; align-items: center; gap: .75rem; min-height: 2.75rem; font-size: .875rem; cursor: pointer; }
.oh-visibility small { display: block; color: #b7c7be; font-size: .75rem; }
.oh-visibility input { position: absolute; left: 0; width: 3rem; height: 2.75rem; opacity: 0; }
.oh-switch-track { position: relative; flex: 0 0 3rem; height: 1.75rem; border: 1px solid #73897d; border-radius: 1rem; background: #32493b; pointer-events: none; }
.oh-switch-track::after { content: ''; position: absolute; top: .1875rem; left: .1875rem; width: 1.25rem; height: 1.25rem; border-radius: 50%; background: #edf5f0; transition: left .15s; }
.oh-visibility input:checked + .oh-switch-track { background: #009640; border-color: #70dfa0; }
.oh-visibility input:checked + .oh-switch-track::after { left: 1.4375rem; }
.oh-visibility input:focus-visible + .oh-switch-track { outline: 2px solid #95ecba; outline-offset: 3px; }
.oh-visibility input:disabled ~ span { opacity: .5; }
.oh-studio-picker { max-width: 32rem; }
.oh-answer-summary, .oh-questionnaire { padding: 1.125rem; border: 1px solid rgba(255, 255, 255, .13); border-radius: .875rem; margin: 1rem 0 1.5rem; background: rgba(0, 0, 0, .1); }
.oh-summary-groups { display: grid; gap: .75rem 1.5rem; grid-template-columns: repeat(auto-fit, minmax(min(100%, 17rem), 1fr)); }
.oh-summary-groups h4 { margin: .75rem 0 .5rem; color: #b4e7c9; }
.oh-category-note.oh-form { gap: .375rem; margin-top: 1.5rem; }
.oh-summary-points { margin: 0; padding-left: 1.125rem; font-size: 1rem; }
.oh-summary-points li { margin: .25rem 0; overflow-wrap: anywhere; }
.oh-progress { appearance: none; width: 100%; height: .375rem; border: 0; border-radius: 1rem; overflow: hidden; margin: 1rem 0 1.25rem; background: #354b3e; color: #70dfa0; }
.oh-progress::-webkit-progress-bar { background: #354b3e; }
.oh-progress::-webkit-progress-value { background: #70dfa0; }
.oh-progress::-moz-progress-bar { background: #70dfa0; }
.oh-question-options { min-width: 0; margin-top: 1.25rem; padding: 0; border: 0; }
.oh-question-options legend { padding: 0 0 .75rem; font-size: .875rem; font-weight: 500; color: #b4cbbd; }
.oh-options-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .75rem; }
.oh-question-actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: .625rem; margin-top: 1.25rem; }
.oh-question-actions > :first-child { margin-right: auto; }
.oh-button:disabled { opacity: .5; cursor: default; }
.oh-button:focus-visible, .oh-tabs button:focus-visible, summary:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible { outline: 2px solid #95ecba; outline-offset: 3px; }
.oh-content { padding: 0 1.25rem 1.25rem; }
.oh-tabs { display: flex; gap: .375rem; flex-wrap: wrap; padding: .75rem 0; border-top: 1px solid rgba(255, 255, 255, .12); margin-bottom: .75rem; }
.oh-tabs button { font-family: inherit; font-size: .875rem; color: #d5e1da; padding: .625rem .875rem; min-height: 2.75rem; border: 1px solid transparent; border-radius: .625rem; }
.oh-tabs button.active { border-color: #528367; background: rgba(112, 223, 160, .12); color: #95ecba; }
.oh-tabs button:disabled { opacity: .5; cursor: default; }
.oh-intro { font-size: 1rem; color: #d5e1da; }
.oh-area { margin-top: 1.5rem; }
.oh-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 16rem), 1fr)); gap: 1rem; margin-top: .75rem; }
.oh-option { position: relative; display: flex; min-width: 0; cursor: pointer; }
.oh-option input { position: absolute; inset: 0; z-index: 1; width: 100%; height: 100%; margin: 0; opacity: 0; cursor: pointer; }
.oh-option-surface { display: grid; grid-template-columns: minmax(0, 1fr) 1.5rem; align-items: center; gap: 1rem; width: 100%; min-height: 4.75rem; padding: 1rem 1.125rem; border: 1px solid #40554a; border-radius: .875rem; background: rgba(255, 255, 255, .025); transition: border-color .15s, background-color .15s; }
.oh-option-title { font-size: 1rem; line-height: 1.5; color: #e1e9e4; overflow-wrap: anywhere; }
.oh-option-mark { position: relative; width: 1.5rem; height: 1.5rem; border: 1px solid #74887c; border-radius: 50%; background: rgba(0, 0, 0, .12); }
.oh-option-mark::after { content: ''; position: absolute; left: .5rem; top: .25rem; width: .375rem; height: .6875rem; border: solid #102d1d; border-width: 0 2px 2px 0; transform: rotate(45deg); opacity: 0; }
.oh-option input:checked + .oh-option-surface { border-color: #70dfa0; background: rgba(112, 223, 160, .11); }
.oh-option input:checked + .oh-option-surface .oh-option-title { color: #effcf4; }
.oh-option input:checked + .oh-option-surface .oh-option-mark { background: #70dfa0; border-color: #70dfa0; }
.oh-option input:checked + .oh-option-surface .oh-option-mark::after { opacity: 1; }
.oh-option input:focus-visible + .oh-option-surface { outline: 2px solid #95ecba; outline-offset: 3px; }
.oh-option input:disabled { cursor: default; }
.oh-option input:disabled + .oh-option-surface { opacity: .5; }
@media (hover: hover) and (pointer: fine) {
  .oh-option input:not(:disabled):hover + .oh-option-surface { border-color: #95ecba; background: rgba(112, 223, 160, .08); }
}
@media (prefers-reduced-motion: reduce) {
  .oh-option-surface { transition: none; }
}
.oh-save, .oh-row, .oh-actions, .oh-refresh { display: flex; align-items: center; justify-content: space-between; gap: .75rem; flex-wrap: wrap; }
.oh-save { margin: 1rem 0 1.5rem; padding: 1rem; border-radius: .75rem; background: rgba(0, 0, 0, .18); }
.oh-actions { justify-content: flex-end; }
.oh-form { display: grid; gap: 1rem; margin: 1rem 0; }
.oh-form label { display: grid; gap: .4rem; font-size: .875rem; color: #d5e1da; }
.oh-form input, .oh-form textarea, .oh-form select { display: block; width: 100%; min-width: 0; max-width: 100%; padding: .75rem; border: 1px solid #62776b; border-radius: .5rem; color: #fff; background: #17281f; font-family: inherit; font-size: 1rem; line-height: 1.5; }
.oh-form select { appearance: auto; }
.oh-form textarea { resize: vertical; }
.oh-message, .oh-notice { display: flex; gap: .75rem; align-items: center; flex-wrap: wrap; padding: .75rem 1rem; font-size: .875rem; }
.oh-notice { background: rgba(112, 223, 160, .08); border-left: 3px solid #70dfa0; }
.oh-error { color: #ffc9c4; background: rgba(190, 60, 45, .12); }
.oh-success { color: #95ecba; }
summary { cursor: pointer; min-height: 2.75rem; padding: .5rem 0; }
.oh-studio-list { margin-top: .75rem; border-top: 1px solid rgba(255, 255, 255, .13); }
.oh-studio { border-bottom: 1px solid rgba(255, 255, 255, .13); }
.oh-studio > summary { display: grid; grid-template-columns: 1rem minmax(0, 1fr) minmax(9rem, auto) 4.5rem; align-items: center; gap: .625rem; padding: .5rem .25rem; list-style: none; }
.oh-studio > summary::-webkit-details-marker { display: none; }
.oh-studio > summary::before { content: '+'; color: #95ecba; }
.oh-studio[open] > summary::before { content: '−'; }
.oh-studio-name { font-size: 1rem; overflow-wrap: anywhere; }
.oh-studio > summary > .oh-meta { text-align: right; }
.oh-studio-detail { padding: 0 .25rem .75rem 1.875rem; }
.oh-studio-detail .oh-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 16rem), 1fr)); column-gap: 1.5rem; }
.oh-list { margin: .5rem 0; padding-left: 1.25rem; font-size: 1rem; }
.oh-list li { padding: .25rem 0; }
.oh-archived-point { padding: .5rem 0; border-bottom: 1px solid rgba(255, 255, 255, .12); }
.oh-catalog-help { margin: .375rem 0 .75rem; }
.oh-catalog-help > summary { min-height: 2.75rem; width: fit-content; }
.oh-catalog-help p { max-width: 48rem; }
.oh-catalog-layout { display: grid; grid-template-columns: 15rem minmax(0, 1fr); gap: 1.5rem; align-items: start; }
.oh-catalog-nav { padding: .5rem; border: 1px solid rgba(255, 255, 255, .12); border-radius: .875rem; background: rgba(0, 0, 0, .12); }
.oh-catalog-nav button { display: flex; align-items: center; justify-content: space-between; gap: .5rem; width: 100%; min-height: 2.75rem; padding: .625rem .75rem; border: 1px solid transparent; border-radius: .625rem; text-align: left; font-size: .875rem; color: #d5e1da; }
.oh-catalog-nav button.active { background: rgba(112, 223, 160, .12); border-color: #528367; color: #95ecba; }
.oh-catalog-nav button:focus-visible, .oh-catalog-usage:focus-visible { outline: 2px solid #95ecba; outline-offset: 2px; }
.oh-catalog-nav .oh-badge { flex: 0 0 auto; min-width: 1.75rem; text-align: center; }
.oh-catalog-group + .oh-catalog-group { margin-top: .75rem; padding-top: .25rem; border-top: 1px solid rgba(255, 255, 255, .1); }
.oh-catalog-group h4 { margin: .875rem .75rem .375rem; font-size: .75rem; font-weight: 500; color: #aabeb2; }
.oh-catalog-main { min-width: 0; }
.oh-catalog-tools.oh-form { margin: 0 0 1rem; }
.oh-catalog-tools input { min-height: 2.75rem; padding: .625rem .75rem; }
.oh-catalog-tools .oh-catalog-mobile-filter { display: none; }
.oh-catalog-heading { margin-bottom: .5rem; }
.oh-catalog-heading h4 { margin: 0; }
.oh-catalog-list { border-top: 1px solid rgba(255, 255, 255, .15); }
.oh-catalog-item { border-bottom: 1px solid rgba(255, 255, 255, .12); }
.oh-catalog-row { display: grid; grid-template-columns: minmax(0, 1fr) 8rem 2.75rem; align-items: center; gap: .75rem; padding: .5rem 0; }
.oh-catalog-title { display: grid; gap: .125rem; min-width: 0; overflow-wrap: anywhere; }
.oh-catalog-title strong { font-size: 1rem; font-weight: 500; }
.oh-catalog-usage { display: flex; align-items: center; justify-content: flex-end; gap: .375rem; min-height: 2.75rem; padding: .375rem; border-radius: .5rem; color: #b4e7c9; font-size: .875rem; }
.oh-catalog-usage .v-icon { color: inherit; }
.oh-catalog-usage:disabled { color: #aab8b0; cursor: default; }
.oh-catalog-edit { width: 2.75rem; padding: .5rem; }
.oh-catalog-studios { display: flex; flex-wrap: wrap; gap: .375rem; padding: 0 0 .75rem; }
@media (max-width: 63.9375rem) {
  .oh-catalog-layout { grid-template-columns: minmax(0, 1fr); gap: 0; }
  .oh-catalog-nav { display: none; }
  .oh-catalog-tools.oh-form { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .oh-catalog-tools .oh-catalog-mobile-filter { display: grid; }
}
.oh-refresh { margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid rgba(255, 255, 255, .12); }
.oh-dialog { background: #14251d !important; font-size: 1rem; }
.oh-dialog-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: .75rem; padding: 1rem 1.5rem !important; }
@media (max-width: 37.4375rem) {
  .oh-header { padding: 1rem; gap: .75rem; }
  .oh-header > .oh-button { width: 100%; }
  .oh-content { padding: 0 1rem 1rem; }
  .oh-management-bar { padding: 0 1rem 1rem; }
  .oh-answer-summary, .oh-questionnaire { padding: .875rem; }
  .oh-options-grid { grid-template-columns: minmax(0, 1fr); gap: .625rem; }
  .oh-option-surface { min-height: 4.25rem; padding: .875rem 1rem; }
  .oh-question-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .oh-question-actions > :first-child { margin-right: 0; }
  .oh-question-actions > :last-child { grid-column: 1 / -1; }
  .oh-save { padding: .75rem; }
  .oh-save .oh-actions, .oh-save .oh-button { width: 100%; }
  .oh-tabs { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .oh-studio > summary { grid-template-columns: 1rem minmax(0, 1fr) auto; row-gap: .25rem; }
  .oh-studio-name { grid-column: 2 / -1; }
  .oh-studio > summary > .oh-badge { grid-column: 2; }
  .oh-studio > summary > .oh-meta { grid-column: 3; }
  .oh-dialog-actions .oh-button { flex: 1; }
  .oh-catalog-tools.oh-form { grid-template-columns: minmax(0, 1fr); }
  .oh-catalog-row { grid-template-columns: minmax(0, 1fr) 2.75rem; gap: .25rem .75rem; }
  .oh-catalog-title { grid-column: 1; }
  .oh-catalog-usage { grid-column: 1; grid-row: 2; justify-content: flex-start; width: fit-content; }
  .oh-catalog-edit { grid-column: 2; grid-row: 1 / 3; }
}
</style>
